import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Cropper, { Area } from 'react-easy-crop';
import { X, Image as ImageIcon, Loader2, Upload, Link, Camera, Crop, ZoomIn, ZoomOut, Check, RotateCw } from 'lucide-react';
import { mediaService, ThumbnailUploadResult } from '../../services/media';

type Mode = 'upload' | 'link';

interface ImageEditorProps {
  value?: string;
  onChange: (url: string, mediaId?: string) => void;
  uploadFn?: (file: File, targetType?: string, targetId?: string) => Promise<ThumbnailUploadResult>;
  deleteFn?: (id: string) => Promise<void>;
  targetType?: string;
  targetId?: string;
  shape?: 'rect' | 'circle';
  aspect?: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  disabled?: boolean;
  accept?: string;
  maxSize?: number;
  label?: string;
  placeholder?: string;
}

const SIZE_MAP = { sm: 80, md: 192, lg: 256 } as const;

// ---- Canvas crop helper ----
async function getCroppedBlob(
  imageSrc: string,
  pixelCrop: Area,
  rotation: number,
): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;

  const rRad = (rotation * Math.PI) / 180;
  const { width: bW, height: bH } = getRotatedSize(image.width, image.height, rotation);

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.translate(-pixelCrop.x, -pixelCrop.y);
  ctx.translate(bW / 2, bH / 2);
  ctx.rotate(rRad);
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas export failed'))),
      'image/jpeg',
      0.92,
    );
  });
}

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', (e) => reject(e));
    img.crossOrigin = 'anonymous';
    img.src = url;
  });
}

function getRotatedSize(w: number, h: number, deg: number) {
  const r = (deg * Math.PI) / 180;
  return {
    width: Math.abs(Math.cos(r) * w) + Math.abs(Math.sin(r) * h),
    height: Math.abs(Math.sin(r) * w) + Math.abs(Math.cos(r) * h),
  };
}

// ---- Component ----
export default function ImageEditor({
  value,
  onChange,
  uploadFn,
  deleteFn,
  targetType,
  targetId,
  shape = 'rect',
  aspect,
  size = 'md',
  className = '',
  disabled = false,
  accept = 'image/jpeg,image/png,image/webp',
  maxSize = 5 * 1024 * 1024,
  label,
  placeholder,
}: ImageEditorProps) {
  const { t } = useTranslation('common');
  const [mode, setMode] = useState<Mode>('upload');
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(value || null);
  const [mediaId, setMediaId] = useState<string | null>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Crop state
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);

  const px = SIZE_MAP[size];

  useEffect(() => {
    if (value && value !== preview) {
      setPreview(value);
    } else if (!value && preview) {
      setPreview(null);
    }
  }, [value]);

  // Open the crop editor with raw image data URL
  const openCropEditor = useCallback((dataUrl: string) => {
    setCropSrc(dataUrl);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedArea(null);
    setError(null);
  }, []);

  // Validate file, then read as data URL and open crop editor
  const handleFileSelected = useCallback((file: File) => {
    const acceptTypes = accept.split(',').map((t) => t.trim());
    if (!acceptTypes.some((t) => file.type === t || (t.endsWith('/*') && file.type.startsWith(t.replace('/*', '/'))))) {
      if (!file.type.startsWith('image/')) {
        setError(t('imageEditor.selectImageFile'));
        return;
      }
    }
    if (file.size > maxSize) {
      setError(t('imageEditor.imageTooLarge', { size: Math.round(maxSize / 1024 / 1024) }));
      return;
    }
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => openCropEditor(e.target?.result as string);
    reader.readAsDataURL(file);
  }, [accept, maxSize, openCropEditor]);

  // After cropping, produce blob and upload
  const handleCropConfirm = useCallback(async () => {
    if (!cropSrc || !croppedArea) return;

    setIsUploading(true);
    setCropSrc(null);

    try {
      const blob = await getCroppedBlob(cropSrc, croppedArea, rotation);
      const file = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });

      // Show local preview immediately
      setPreview(URL.createObjectURL(blob));

      const doUpload = uploadFn || mediaService.uploadThumbnail;
      const result = await doUpload(file, targetType, targetId);
      setMediaId(result._id);
      onChange(result.url, result._id);
      setPreview(result.url);
    } catch (err: any) {
      const isTimeout = err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT' || err?.code === 'ERR_NETWORK';
      const fallbackMsg = isTimeout ? t('imageEditor.uploadTimeout') : t('imageEditor.failedToUpload');
      setError(err.response?.data?.error || fallbackMsg);
      setPreview(value || null);
    } finally {
      setIsUploading(false);
    }
  }, [cropSrc, croppedArea, rotation, uploadFn, targetType, targetId, onChange, value]);

  const handleCropCancel = useCallback(() => {
    setCropSrc(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled || isUploading) return;
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelected(file);
  }, [disabled, isUploading, handleFileSelected]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled && !isUploading) setIsDragging(true);
  }, [disabled, isUploading]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelected(file);
    if (e.target) e.target.value = '';
  }, [handleFileSelected]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    if (disabled || isUploading || cropSrc) return;
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) handleFileSelected(file);
        return;
      }
    }

    if (mode === 'link') {
      const text = e.clipboardData.getData('text/plain').trim();
      if (text && (text.startsWith('http://') || text.startsWith('https://'))) {
        e.preventDefault();
        setLinkUrl(text);
        setPreview(text);
        onChange(text);
      }
    }
  }, [disabled, isUploading, cropSrc, handleFileSelected, onChange, mode]);

  const handleRemove = useCallback(async () => {
    if (mediaId) {
      try {
        const doDelete = deleteFn || mediaService.deleteThumbnail;
        await doDelete(mediaId);
      } catch (err) {
        console.error('Failed to delete image:', err);
      }
    }
    setPreview(null);
    setMediaId(null);
    setLinkUrl('');
    onChange('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [mediaId, onChange, deleteFn]);

  const handleClick = useCallback(() => {
    if (!disabled && !isUploading) fileInputRef.current?.click();
  }, [disabled, isUploading]);

  const handleLinkSubmit = useCallback(() => {
    const url = linkUrl.trim();
    if (!url) return;
    setPreview(url);
    onChange(url);
  }, [linkUrl, onChange]);

  const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCroppedArea(croppedAreaPixels);
  }, []);

  const tabs: { key: Mode; icon: typeof Upload; label: string }[] = [
    { key: 'upload', icon: Upload, label: t('imageEditor.upload') },
    { key: 'link', icon: Link, label: t('imageEditor.link') },
  ];

  // ---- Crop Editor (modal overlay) ----
  if (cropSrc) {
    return (
      <div className={className}>
        {label && <label className="block text-sm font-medium text-gray-600 mb-2">{label}</label>}
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-gray-900">
          {/* Crop area */}
          <div className="relative w-full" style={{ height: 300 }}>
            <Cropper
              image={cropSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect ?? (shape === 'circle' ? 1 : 16 / 9)}
              cropShape={shape === 'circle' ? 'round' : 'rect'}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
              showGrid
            />
          </div>

          {/* Controls */}
          <div className="bg-white px-4 py-3 space-y-3">
            {/* Zoom */}
            <div className="flex items-center gap-3">
              <ZoomOut className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <input
                type="range"
                min={1}
                max={3}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1 accent-gold h-1.5"
              />
              <ZoomIn className="w-4 h-4 text-gray-400 flex-shrink-0" />
            </div>

            {/* Action buttons */}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setRotation((r) => (r + 90) % 360)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <RotateCw className="w-3.5 h-3.5" />
                {t('imageEditor.rotate')}
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCropCancel}
                  className="flex items-center gap-1.5 px-4 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                  {t('imageEditor.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleCropConfirm}
                  className="flex items-center gap-1.5 px-4 py-1.5 text-sm text-white bg-gold rounded-lg hover:bg-amber-600 transition-colors"
                >
                  <Check className="w-3.5 h-3.5" />
                  {t('imageEditor.apply')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- Preview State ----
  if (preview) {
    const actionBar = !disabled && (
      <div className="flex items-center gap-2 mt-2">
        <button
          type="button"
          onClick={handleClick}
          disabled={isUploading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
        >
          <Upload className="w-3.5 h-3.5" />
          {t('imageEditor.replace')}
        </button>
        <button
          type="button"
          onClick={() => {
            // Re-open crop editor on existing preview image
            openCropEditor(preview);
          }}
          disabled={isUploading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
        >
          <Crop className="w-3.5 h-3.5" />
          {t('imageEditor.edit')}
        </button>
        <button
          type="button"
          onClick={handleRemove}
          disabled={isUploading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
        >
          <X className="w-3.5 h-3.5" />
          {t('imageEditor.remove')}
        </button>
      </div>
    );

    if (shape === 'circle') {
      return (
        <div className={className} tabIndex={0} onPaste={handlePaste}>
          {label && <label className="block text-sm font-medium text-gray-600 mb-2">{label}</label>}
          <input ref={fileInputRef} type="file" accept={accept} onChange={handleFileInput} className="hidden" disabled={disabled || isUploading} />
          <div className="relative inline-block" style={{ width: px, height: px }}>
            <img
              src={preview}
              alt={t('imageEditor.preview')}
              className="w-full h-full rounded-full object-cover border border-gray-200"
            />
            {isUploading && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-full">
                <Loader2 className="w-6 h-6 text-gold animate-spin" />
              </div>
            )}
          </div>
          {actionBar}
          {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
        </div>
      );
    }

    // rect shape preview
    return (
      <div className={className} tabIndex={0} onPaste={handlePaste}>
        {label && <label className="block text-sm font-medium text-gray-600 mb-2">{label}</label>}
        <input ref={fileInputRef} type="file" accept={accept} onChange={handleFileInput} className="hidden" disabled={disabled || isUploading} />
        <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-white w-fit">
          <img src={preview} alt="Preview" className="max-w-full object-cover" style={{ aspectRatio: aspect ?? 16 / 9, maxHeight: px }} />
          {isUploading && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-gold animate-spin" />
            </div>
          )}
        </div>
        {actionBar}
        {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
      </div>
    );
  }

  // ---- Empty State ----
  return (
    <div className={className} onPaste={handlePaste}>
      {label && <label className="block text-sm font-medium text-gray-600 mb-2">{label}</label>}

      {/* Mode tabs */}
      <div className="flex border-b border-gray-200 mb-3">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => { setMode(tab.key); setError(null); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm -mb-px transition-colors ${
              mode === tab.key
                ? 'border-b-2 border-gold text-gold font-medium'
                : 'text-gray-500 hover:text-gray-600'
            }`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileInput}
        className="hidden"
        disabled={disabled || isUploading}
      />

      {/* Upload mode */}
      {mode === 'upload' && (
        <div
          tabIndex={0}
          onClick={handleClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`
            border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer outline-none
            focus:border-gold focus:ring-2 focus:ring-gold/30
            ${isDragging ? 'border-gold bg-gold/10' : 'border-gray-200 hover:border-gray-300'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          {isUploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-8 h-8 text-gold animate-spin" />
              <span className="text-gray-500 text-sm">{t('imageEditor.uploading')}</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="p-3 bg-gray-100 rounded-full">
                {shape === 'circle' ? (
                  <Camera className="w-5 h-5 text-gray-500" />
                ) : (
                  <ImageIcon className="w-5 h-5 text-gray-500" />
                )}
              </div>
              <div className="text-gray-600 text-sm font-medium">
                {placeholder || t('imageEditor.dropPasteOrClick')}
              </div>
              <div className="text-gray-500 text-xs">
                JPEG, PNG or WebP (max {Math.round(maxSize / 1024 / 1024)}MB)
              </div>
            </div>
          )}
        </div>
      )}

      {/* Link mode */}
      {mode === 'link' && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://..."
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleLinkSubmit(); } }}
              disabled={disabled}
            />
            <button
              type="button"
              onClick={handleLinkSubmit}
              disabled={disabled || !linkUrl.trim()}
              className="px-3 py-2 bg-gold text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Set
            </button>
          </div>
          {linkUrl.trim() && (linkUrl.startsWith('http://') || linkUrl.startsWith('https://')) && (
            <div className="rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
              <img
                src={linkUrl}
                alt="Link preview"
                className={shape === 'circle' ? 'w-20 h-20 rounded-full object-cover mx-auto my-3' : 'max-w-full object-cover'}
                style={shape !== 'circle' ? { aspectRatio: aspect ?? 16 / 9, maxHeight: px } : undefined}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            </div>
          )}
        </div>
      )}

      {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
    </div>
  );
}
